<script setup>
import { computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useThemeColors } from 'dashboard/composables/useThemeColors';
import DonutChart from 'shared/components/charts/DonutChart.vue';

// eslint-disable-next-line no-unused-vars
const store = useStore();
const teamSummaryData = useMapGetter('summaryReports/getTeamSummaryReports');
const { getChartColors } = useThemeColors();

const chartData = computed(() => {
  const dataToUse = teamSummaryData.value;

  if (!dataToUse || dataToUse.length === 0) {
    return { labels: [], datasets: [] };
  }

  const totalConversations = dataToUse.reduce((sum, team) => {
    return sum + (team.conversationsCount || 0);
  }, 0);

  if (totalConversations === 0) {
    return { labels: [], datasets: [] };
  }

  const teamsWithConversations = dataToUse
    .filter(team => (team.conversationsCount || 0) > 0)
    .map(team => ({
      name: team.name || 'Equipe sem nome',
      count: team.conversationsCount || 0,
      percentage: Math.round(
        ((team.conversationsCount || 0) / totalConversations) * 100
      ),
    }))
    .sort((a, b) => b.count - a.count);

  const chartColors = getChartColors();

  return {
    labels: teamsWithConversations.map(team => team.name),
    datasets: [
      {
        data: teamsWithConversations.map(team => team.percentage),
        backgroundColor: chartColors.chartPalette.slice(
          0,
          teamsWithConversations.length
        ),
        borderWidth: 2,
        borderColor: chartColors.background,
      },
    ],
  };
});

const chartOptions = computed(() => {
  const chartColors = getChartColors();

  return {
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          generateLabels: chart => {
            const data = chart.data;
            if (data.labels && data.datasets && data.datasets[0]) {
              return data.labels.map((label, index) => ({
                text: `${label}: ${data.datasets[0].data[index]}%`,
                fillStyle: data.datasets[0].backgroundColor[index],
                strokeStyle:
                  data.datasets[0].borderColor || chartColors.background,
                lineWidth: data.datasets[0].borderWidth || 1,
                hidden: false,
                index: index,
                fontColor: chartColors.textPrimary,
              }));
            }
            return [];
          },
          padding: 10,
          boxWidth: 12,
          font: {
            size: 12,
          },
          color: chartColors.textPrimary,
          usePointStyle: false,
        },
      },
      tooltip: {
        backgroundColor: chartColors.tooltipBg,
        titleColor: chartColors.textPrimary,
        bodyColor: chartColors.textSecondary,
        borderColor: chartColors.tooltipBorder,
        borderWidth: 1,
        callbacks: {
          label: context => {
            const label = context.label || '';
            const percentage = context.parsed || 0;
            return `${label}: ${percentage}%`;
          },
        },
      },
    },
  };
});

const hasData = computed(() => {
  return chartData.value.labels.length > 0;
});
</script>

<template>
  <div
    class="bg-n-solid-2 rounded-xl p-4 shadow outline-1 outline outline-n-container w-full max-w-full"
  >
    <h3 class="text-lg font-medium text-n-slate-12 mb-4 text-center">
      {{ $t('REPORT.TEAM_CONVERSATIONS_CHART_TITLE') }}
    </h3>
    <div v-if="hasData" class="h-72 w-full">
      <DonutChart :collection="chartData" :chart-options="chartOptions" />
    </div>
    <div v-else class="flex items-center justify-center h-72">
      <span class="text-sm text-slate-600">
        {{ $t('REPORT.NO_ENOUGH_DATA') }}
      </span>
    </div>
  </div>
</template>
